import React, { useState } from 'react';
import { useSet } from 'react-use';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { useWhatIfGroupsQuery, WhatIfTypeDTO } from './api/useWhatIfGroupsQuery';
import { WhatIf } from './components/WhatIf/WhatIf';
import { WhatIfGroup } from './components/WhatIfGroup/WhatIfGroup';
import { WhatIfsHeader } from './components/WhatIfsHeader/WhatIfsHeader';
import { WhatIfsError } from './WhatIfs.error';
import { LoadingWhatIfs } from './WhatIfs.loading';
import { WHATIF_GROUP_LABEL } from './WhatIfs.meta';
import { SectionHeader, StyledWrapper } from './WhatIfs.styles';

interface WhatIfsProps {
  clientId: number;
  fpId: string;
  whatIfAreaId: string;
  goalId?: string;
  onClose: () => void;
}

export const WhatIfs = ({ clientId, fpId, goalId, onClose, whatIfAreaId }: WhatIfsProps) => {
  const [, { has: isGroupExpanded, toggle: toggleGroupExpansion }] = useSet();
  const [activeWhatIfType, setActiveWhatIfType] = useState<WhatIfTypeDTO | null>(null);

  const { data, isLoading, isError, error, refetch } = useWhatIfGroupsQuery({
    clientId,
    fpId,
    whatIfAreaId,
    goalId,
  });

  if (isLoading) {
    return <LoadingWhatIfs onClose={onClose} />;
  }

  if (isError) {
    return <WhatIfsError onClose={onClose} error={error} onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  return (
    <StyledWrapper>
      <WhatIfsHeader onClose={onClose} />

      {activeWhatIfType === null ? (
        <>
          <SectionHeader>
            <Text as="h3" fontSize="small" fontWeight={600} color="neutral.500">
              GOAL WHAT-IFS
            </Text>
          </SectionHeader>

          {Object.entries(data).map(([whatIfGroupId, whatIfTypes]) =>
            whatIfTypes.length > 0 ? (
              <WhatIfGroup
                key={whatIfGroupId}
                id={whatIfGroupId}
                label={WHATIF_GROUP_LABEL[whatIfGroupId]}
                isExpanded={isGroupExpanded(whatIfGroupId)}
                onExpanded={toggleGroupExpansion}
                onWhatIfTypeChange={setActiveWhatIfType}
                whatIfTypes={whatIfTypes}
              />
            ) : (
              <EmptyState m="56" message="There are no what-ifs available" />
            ),
          )}
        </>
      ) : (
        <WhatIf
          clientId={clientId}
          fpId={fpId}
          goalId={goalId}
          whatIfAreaId={whatIfAreaId}
          onBack={() => setActiveWhatIfType(null)}
          onSuccess={() => {
            toast.success('The what-if was successfully added.');

            setActiveWhatIfType(null);
          }}
          onDelete={() => {
            toast.success('The what-if was successfully deleted.');

            setActiveWhatIfType(null);
          }}
          {...activeWhatIfType}
        />
      )}
    </StyledWrapper>
  );
};
